import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import CheckboxDropdown, { CheckboxDropdownOption } from 'commons/src/components/dropdown/CheckboxDropdown';
import { DeviceWithKeyInfo, SegmentProperties, AnyDeviceType, BuildingType } from 'commons/src/models/commonTypeScript';
import { fetchSegmentProperties } from '../../../actions/segmentPropertiesActions';
import { Store } from '../../../reducers';

export type Props = {
    building: BuildingType | undefined;
    selectedDevices: DeviceWithKeyInfo[];
    setSelectedDevices: (devices: DeviceWithKeyInfo[]) => void;
    acceptedDeviceTypes: AnyDeviceType[];
};

const WellReportDeviceDropdown = ({
    building,
    selectedDevices,
    setSelectedDevices,
    acceptedDeviceTypes,
}: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();

    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);
    const segmentProperties = useSelector((state: Store) => state.segmentPropertiesStore.segmentProperties);

    const allAcceptedDevices = useMemo(() => {
        if (!building) return [];
        return building.devices
            .map(serialNumber => devices[serialNumber])
            .filter(
                (device): device is DeviceWithKeyInfo =>
                    device !== undefined && acceptedDeviceTypes.includes(device.type)
            );
    }, [building, devices, acceptedDeviceTypes]);

    const getDeviceLocation = useCallback(
        (device: DeviceWithKeyInfo): string => {
            const segmentProps: SegmentProperties | undefined = segmentProperties[device.serialNumber];
            if (!segmentProps) return '';

            const parts: string[] = [];
            if (segmentProps.floor !== undefined) {
                parts.push(`${txt('Floor')} ${segmentProps.floor}`);
            }
            if (segmentProps.roomType) {
                parts.push(txt(`RoomTypeOptions.${segmentProps.roomType}`));
            }
            return parts.join(' • ');
        },
        [segmentProperties, txt]
    );

    const deviceOptions = useMemo<CheckboxDropdownOption<string>[]>(
        () =>
            allAcceptedDevices.map(device => ({
                id: device.serialNumber,
                label: device.segmentName,
                subLabel: getDeviceLocation(device) || undefined,
                searchText: device.segmentName,
            })),
        [allAcceptedDevices, getDeviceLocation]
    );

    const selectedDeviceIds = useMemo(() => selectedDevices.map(device => device.serialNumber), [selectedDevices]);

    const handleSelectionChange = useCallback(
        (selectedIds: string[]): void => {
            const selectedDevicesList = allAcceptedDevices.filter(device => selectedIds.includes(device.serialNumber));
            setSelectedDevices(selectedDevicesList);

            selectedIds.forEach(serialNumber => {
                if (!segmentProperties[serialNumber]) {
                    dispatch(fetchSegmentProperties(serialNumber));
                }
            });
        },
        [allAcceptedDevices, setSelectedDevices, segmentProperties, dispatch]
    );

    const countLabel = useCallback((count: number): string => `${count} ${txt('DevicesLowerCase')} selected`, [txt]);

    return (
        <CheckboxDropdown
            options={deviceOptions}
            selectedIds={selectedDeviceIds}
            onSelectionChange={handleSelectionChange}
            confirmButtonText="Add"
            countLabel={countLabel}
            testId="device-dropdown"
            showSelectAll
        />
    );
};

export default WellReportDeviceDropdown;
